import type { TaskTopic } from "../types/task";
import { ref } from "vue";
import { posthogService } from "./posthogService";

const STORAGE_KEY = "timeTracker_topics";

const DEFAULT_TOPICS: TaskTopic[] = [
  { id: "coding", name: "Coding" },
  { id: "meeting", name: "Meeting" },
  { id: "planning", name: "Planning" },
  { id: "research", name: "Research" },
  { id: "documentation", name: "Documentation" },
  { id: "design", name: "Design" },
  { id: "personal", name: "Personal" },
  { id: "in-between", name: "In-Between", system: true },
  { id: "other", name: "Other" },
];

// Create a reactive reference to store topics
export const topics = ref<TaskTopic[]>([]);

// Initialize topics from localStorage or defaults
const topicsJson = localStorage.getItem(STORAGE_KEY);
if (!topicsJson) {
  topics.value = DEFAULT_TOPICS;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_TOPICS));
} else {
  topics.value = JSON.parse(topicsJson);

  // Migration: Add 'Personal' topic if it doesn't exist
  const personalTopicExists = topics.value.some(
    (topic) => topic.name === "Personal"
  );
  if (!personalTopicExists) {
    const personalTopic: TaskTopic = { id: "personal", name: "Personal" };
    topics.value.push(personalTopic);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(topics.value));
  }

  // Migration: Add 'In-Between' topic if it doesn't exist
  const inBetweenTopicExists = topics.value.some(
    (topic) => topic.name === "In-Between"
  );
  if (!inBetweenTopicExists) {
    const inBetweenTopic: TaskTopic = {
      id: "in-between",
      name: "In-Between",
      system: true,
    };
    topics.value.push(inBetweenTopic);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(topics.value));
  } else {
    // Ensure existing 'In-Between' topic is marked as system topic
    const inBetweenTopic = topics.value.find(
      (topic) => topic.name === "In-Between"
    );
    if (inBetweenTopic && !inBetweenTopic.system) {
      inBetweenTopic.system = true;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(topics.value));
    }
  }

  // Migration: Rename legacy 'Idle' topic to 'In-Between'
  // - If a topic with id 'idle' or name 'Idle' exists, convert it to the new
  //   system topic id 'in-between' and name 'In-Between'. If the new topic
  //   already exists, remove the legacy one to avoid duplicates.
  const legacyIdleIndex = topics.value.findIndex(
    (topic) => topic.id === "idle" || topic.name === "Idle"
  );
  if (legacyIdleIndex !== -1) {
    const hasNewInBetween = topics.value.some(
      (topic) => topic.id === "in-between" || topic.name === "In-Between"
    );

    if (hasNewInBetween) {
      // Remove the legacy Idle topic if the new one already exists
      topics.value.splice(legacyIdleIndex, 1);
    } else {
      // Transform the legacy Idle topic into the new system topic
      topics.value[legacyIdleIndex] = {
        id: "in-between",
        name: "In-Between",
        system: true,
        deleted: false,
      } as TaskTopic;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(topics.value));
  }
}

class TopicService {
  getTopics(): TaskTopic[] {
    return topics.value.filter((topic) => !topic.deleted);
  }

  getAllTopics(): TaskTopic[] {
    return topics.value;
  }

  saveTopics(newTopics: TaskTopic[]): void {
    topics.value = newTopics;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newTopics));
  }

  getTopicById(id: string): TaskTopic | undefined {
    return topics.value.find((topic) => topic.id === id && !topic.deleted);
  }

  getTopicByName(name: string): TaskTopic | undefined {
    return topics.value.find((topic) => topic.name === name && !topic.deleted);
  }

  createTopic(name: string): TaskTopic {
    const id = name.toLowerCase().replace(/\s+/g, "-");

    // Check if topic already exists (including soft deleted ones)
    const existingTopic = topics.value.find((topic) => topic.name === name);
    if (existingTopic) {
      if (existingTopic.deleted) {
        // Restore the soft deleted topic
        existingTopic.deleted = false;
        this.saveTopics([...topics.value]);
        return existingTopic;
      }
      return existingTopic;
    }

    const newTopic: TaskTopic = { id, name };
    this.saveTopics([...topics.value, newTopic]);

    // Track topic creation
    posthogService.trackTopicCreated(name);

    return newTopic;
  }

  updateTopic(id: string, name: string): TaskTopic | undefined {
    const topicIndex = topics.value.findIndex((topic) => topic.id === id);

    if (topicIndex === -1) return undefined;

    const topic = topics.value[topicIndex];
    if (topic.system) {
      console.warn("Cannot update system topic:", topic.name);
      return undefined;
    }

    const updatedTopic: TaskTopic = { id, name };
    const newTopics = [...topics.value];
    newTopics[topicIndex] = updatedTopic;
    this.saveTopics(newTopics);
    return updatedTopic;
  }

  softDeleteTopic(id: string): boolean {
    const topicIndex = topics.value.findIndex((topic) => topic.id === id);

    if (topicIndex === -1) return false;

    const topicToDelete = topics.value[topicIndex];
    if (topicToDelete.system) {
      console.warn("Cannot delete system topic:", topicToDelete.name);
      return false;
    }

    const newTopics = [...topics.value];
    newTopics[topicIndex] = { ...newTopics[topicIndex], deleted: true };
    this.saveTopics(newTopics);

    // Track topic deletion
    if (topicToDelete) {
      posthogService.trackTopicDeleted(topicToDelete.name);
    }

    return true;
  }

  restoreTopic(id: string): boolean {
    const topicIndex = topics.value.findIndex((topic) => topic.id === id);

    if (topicIndex === -1) return false;

    const newTopics = [...topics.value];
    newTopics[topicIndex] = { ...newTopics[topicIndex], deleted: false };
    this.saveTopics(newTopics);
    return true;
  }

  deleteTopic(id: string): boolean {
    const filteredTopics = topics.value.filter((topic) => topic.id !== id);

    if (filteredTopics.length === topics.value.length) return false;

    this.saveTopics(filteredTopics);
    return true;
  }

  getOrCreateTopic(name: string): TaskTopic {
    return this.getTopicByName(name) || this.createTopic(name);
  }

  isSystemTopic(id: string): boolean {
    const topic = this.getTopicById(id);
    return topic?.system || false;
  }

  resetToDefaults(): TaskTopic[] {
    // Reset topics to default values
    topics.value = DEFAULT_TOPICS;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_TOPICS));
    console.log("Reset topics to defaults:", DEFAULT_TOPICS);
    return DEFAULT_TOPICS;
  }
}

export const topicService = new TopicService();
